import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import { mq } from '@styles/breakpoints';
import { mixins } from '@styles/mixins';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../generic-icon';
import { ComponentContainer } from '@styles/layout';
import { Item } from '@components/breadcrumbs/item';
import { tripTypeLabels } from '@model/common';

/* ***************** *
 *       Types       *
 * ***************** */
export interface BreadcrumbProps {
  theme?: Theme;
  testId?: string;
  items: Array<Item>;
  tripType?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  display: 'block',
  borderBottom: `1px solid ${theme.custom.colors.group10.light}`
}));

interface BreadcrumbContainerProps extends ThemeProps {
  isScrolled: boolean;
}

const BreadcrumbContainer = styled(ComponentContainer)(({ theme, isScrolled }: BreadcrumbContainerProps) => ({
  display: 'flex',
  alignItems: 'center',
  margin: 0,
  marginLeft: isScrolled ? 0 : theme.custom.spacing.small,
  scrollbarWidth: 'none',
  overflowX: 'auto',
  overflowY: 'hidden',

  [mq.ie()]: {
    minHeight: 0,
    height: 62
  },

  [mq.small]: {
    padding: `0 ${theme.custom.spacing.large}px`,
    margin: '0 auto',
    minHeight: 62
  }
}));

const BreadcrumbList = styled.ul({
  display: 'flex',
  flexWrap: 'nowrap',
  listStyle: 'none',
  padding: 0,
  whiteSpace: 'nowrap'
});

const BreadcrumbLink = styled.span(({ theme }: ThemeProps) => ({
  ...mixins(theme).link
}));

const BreadcrumbListItem = styled.li(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  lineHeight: '2.5rem',
  paddingRight: theme.custom.spacing.small,
  fontSize: '1.4rem',

  ['a']: {
    textDecoration: 'none'
  },

  [mq.small]: {
    paddingRight: theme.custom.spacing.large,
    fontSize: '1.6rem'
  }
}));

const chevronIconStyles = (theme: Theme) => ({
  color: theme.custom.colors.group4.light,
  fontSize: '0.8rem',
  marginLeft: theme.custom.spacing.small,

  [mq.small]: {
    marginLeft: theme.custom.spacing.large
  }
});

/* *********************** *
 *    BreadcrumbComponent  *
 * *********************** */

export const BreadcrumbComponent = (props: BreadcrumbProps) => {
  const { items, theme, tripType } = props;
  const [isScrolled, setIsScrolled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkScroll = () => {
      setIsScrolled(container.scrollLeft > 0);
    };

    container.addEventListener('scroll', checkScroll);
    return () => {
      container.removeEventListener('scroll', checkScroll);
    };
  }, []);

  const renderBreadcrumbItems = () => {
    return items.map((item: Item, index: number) => {
      const isFirstItem = index === 0;
      const isLastItem = index === items.length - 1;

      return (
        <BreadcrumbListItem
          key={`${item.display}-${index}`}
          data-testid={isLastItem ? TestId.breadcrumb.breadcrumbCurrentItem : TestId.breadcrumb.breadcrumbItem}
        >
          {!isLastItem ? (
            <>
              <Link prefetch={false} href={item.href} as={item.as} passHref>
                <BreadcrumbLink data-testid={TestId.breadcrumb.breadcrumbLink}>
                  {item.display}
                  {!!tripType && isFirstItem ? ` ${tripTypeLabels[tripType]}` : ''}
                </BreadcrumbLink>
              </Link>
              <Icon name={'mhi-chevron-right'} css={chevronIconStyles(theme!)} />
            </>
          ) : (
            <>{item.display}</>
          )}
        </BreadcrumbListItem>
      );
    });
  };

  return (
    <Container data-testid={props.testId || TestId.breadcrumb.main}>
      <BreadcrumbContainer ref={containerRef} noPadding isScrolled={isScrolled}>
        <BreadcrumbList>{renderBreadcrumbItems()}</BreadcrumbList>
      </BreadcrumbContainer>
    </Container>
  );
};

export const Breadcrumb = withTheme(BreadcrumbComponent);
